import styles from './QuotaCheckModal.module.scss';
import * as d3 from 'd3';
import { ReactComponent as WarningSign } from '../../assets/warningSign.svg';
import { ReactComponent as AlertSign } from '../../assets/alertSign.svg';
import { ReactComponent as AIIcon } from '../../assets/ai.svg';
import { BreakdownItem } from './BreakdownItem';
import { useTranslation } from 'react-i18next';

type QuotaType = string;

interface Quota {
  is_significant_spend: boolean,
  quota_usage: number,
  can_create: boolean,
  skip_confirmation: boolean,
  quota_balance: number,
  is_low_quota: boolean,
  ai_quota_usage_breakdown?: {
    topic_modeling?: number;
    aspect_based_sentiment?: number;
    stories?: number;
  };
}



const formatNumberSignificant = d3.format('.3~s');
const formatWithCustomGigaBillion = (value: number) => {
  const formattedValue = formatNumberSignificant(value);
  const formattedWithBillion = formattedValue.replace('G', 'B');
  return formattedWithBillion;
};

interface QuotaInfoProps {
  quota: Quota;
  quotaType: QuotaType;
}

export const QuotaInfo: React.FC<QuotaInfoProps> = ({ quota, quotaType }) => {
  const {
    is_significant_spend,
    quota_usage,
    can_create,
    skip_confirmation,
    ai_quota_usage_breakdown,
    quota_balance,
    is_low_quota,
  } = quota;
  const { t } = useTranslation();
  const quotaUsageFormatted = formatWithCustomGigaBillion(quota_usage);
  const aiQuotaBreakdown = ai_quota_usage_breakdown || {};
  const isQuotaInsufficient = quotaType === 'analytical' ? quota_balance <= 0 : quota_balance - quota_usage < 0;
  
  const topicModelingUsage = aiQuotaBreakdown?.topic_modeling
    ? formatWithCustomGigaBillion(aiQuotaBreakdown?.topic_modeling)
    : 0;
  const aspectBasedSentimentUsage = aiQuotaBreakdown?.aspect_based_sentiment
    ? formatWithCustomGigaBillion(aiQuotaBreakdown?.aspect_based_sentiment)
    : 0;
  const storiesUsage = aiQuotaBreakdown?.stories
    ? formatWithCustomGigaBillion(aiQuotaBreakdown?.stories)
    : 0;

  
  const getContainerClass = () => {
    if (quota_usage <= 0) {
      return styles.gray;
    }
    if((is_low_quota || is_significant_spend) && !isQuotaInsufficient) {
      return styles.yellow;
    }
    if (isQuotaInsufficient) {
      return styles.red;
    }
    return styles.gray;
  };

  const getQuotaBalanceClass = () => {
    if (quota_usage <= 0) {
      return '';
    }
    if (is_low_quota && !isQuotaInsufficient) {
      return styles.warning;
    }
    if (isQuotaInsufficient) {
      return styles.alert;
    }
    return '';
  }

  const getQuotaUsageClass = () => {
    if (quota_usage <= 0) {
      return '';
    }
    if (is_significant_spend && !isQuotaInsufficient) {
      return styles.warning;
    }
    return '';
  }

  const containerClass = getContainerClass();
  const quotaBalanceClass = getQuotaBalanceClass();
  const quotaUsageClass = getQuotaUsageClass();

  return (
    <div
      className={`${styles.quotaInfoContainer} ${
      containerClass
      }`}
    >
      <div className={styles.quotaInfo}>
        <div className={styles.quotaInfoHeader}>
          <div className={styles.quotaInfoTitle}>
            {quotaType === 'analytical' ? t('Analytical quota') : t('AI quota')}
          </div>
          <div className={styles.quotaInfoUsage}>
            <span className={quotaUsageClass}>{quota_usage > 0 ? '~' : ''}{quotaUsageFormatted}</span> /{' '}
            <span className={quotaBalanceClass}>
              {formatWithCustomGigaBillion(quota_balance)}
            </span>
          </div>
        </div>
        <div className={styles.quotaWarnings}>
          {(is_significant_spend && !isQuotaInsufficient && quota_usage > 0) && (
            <div className={styles.warning}>
              <WarningSign className={styles.warningIcon} />
              <span className={styles.warningText}>
                {t('High quota usage expected')}
              </span>
            </div>
          )}
          {(is_low_quota && !isQuotaInsufficient && quota_usage > 0) && (
            <div className={styles.warning}>
              <WarningSign className={styles.warningIcon} />
              <span className={styles.warningText}>
                {t('Remaining quota is low')}
              </span>
            </div>
          )}
          {isQuotaInsufficient && quota_usage > 0 && (
            <div className={styles.alert}>
              <AlertSign className={styles.alertIcon} />
              <span className={styles.warningText}>
                {t('Not enough quota')}
              </span>
            </div>
          )}
        </div>

        {quotaType === 'ai' && Object.keys(aiQuotaBreakdown).length > 0 ? (
          <div className={styles.quotaUsageDetails}>
            <BreakdownItem 
              value={aiQuotaBreakdown?.topic_modeling}
              formattedValue={topicModelingUsage}
              label={t('Topic modeling')}
            />
            <BreakdownItem 
              value={aiQuotaBreakdown?.aspect_based_sentiment}
              formattedValue={aspectBasedSentimentUsage}
              label={t('Contextual sentiment')}
            />
            <BreakdownItem 
              value={aiQuotaBreakdown?.stories}
              formattedValue={storiesUsage}
              label={t('Stories')}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
